import {
  isPersistedVizPayload,
  mergeLandingSnapshot,
  parseLandingSnapshotManifest,
  type LandingSnapshotCore,
  type LandingSnapshotManifest,
  type LandingSnapshotManifestEntry,
  type PersistedVizPayload,
  type TemplateDetail,
} from '@roomtreatment/core';

const MANIFEST_URL = '/landing-templates/manifest.json';

type FetchPriority = 'high' | 'low' | 'auto';

async function getJson<T>(
  url: string,
  cache: RequestCache,
  priority?: FetchPriority,
): Promise<T | null> {
  try {
    const res = await fetch(url, priority ? { cache, priority } : { cache });
    if (!res.ok) return null;
    return (await res.json().catch(() => null)) as T | null;
  } catch {
    return null;
  }
}

export async function fetchLandingManifest(): Promise<LandingSnapshotManifest | null> {
  // The manifest URL is stable while its entries change when the landing
  // registry is rebuilt. Revalidate it on every page load so a browser cannot
  // keep an old ID map after a registry deploy; the referenced hashed assets
  // remain force-cached below.
  const raw = await getJson<unknown>(MANIFEST_URL, 'no-cache');
  if (raw == null) return null;
  try {
    return parseLandingSnapshotManifest(raw);
  } catch {
    return null;
  }
}

export async function fetchLandingTemplateCore(
  entry: LandingSnapshotManifestEntry,
): Promise<LandingSnapshotCore | null> {
  const raw = await getJson<LandingSnapshotCore>(entry.coreUrl, 'force-cache');
  if (!raw?.doc || !raw.hash) return null;
  return raw;
}

export async function fetchLandingTemplateViz(
  entry: LandingSnapshotManifestEntry,
  opts?: { priority?: FetchPriority },
): Promise<PersistedVizPayload | null> {
  const raw = await getJson<unknown>(entry.vizUrl, 'force-cache', opts?.priority);
  if (!isPersistedVizPayload(raw)) return null;
  return raw;
}

/** Merge hashed core + viz into the TemplateDetail hydrate path. */
export function mergeLandingTemplateDetail(
  core: LandingSnapshotCore,
  viz: PersistedVizPayload | null,
): TemplateDetail {
  return mergeLandingSnapshot(core, viz);
}

export async function fetchLandingTemplateDetail(
  entry: LandingSnapshotManifestEntry,
): Promise<TemplateDetail | null> {
  const core = await fetchLandingTemplateCore(entry);
  if (!core) return null;
  const viz = await fetchLandingTemplateViz(entry);
  return mergeLandingTemplateDetail(core, viz);
}
